import * as ReactDOM from 'react-dom/client';
import { Capacitor } from '@capacitor/core';
import * as Sentry from '@sentry/capacitor';

import {
	addMultipleEffects,
	bookingEntity,
	bootstrapBusinessLogic,
	BusinessLayerContext,
	dataChangesEntity,
	editBookingEffect,
	editLegEffect,
	// Note: featureFlag featureFlagEntity is added here for the driver app
	// because of shared MplPageHeader component
	featureFlagEntity,
	fetchUserEffect,
	initDatabase,
	initEventStreamEffect,
	initPushProcessorEffect,
	LogoutWrapper,
	resetScheduleDiffEffect,
	scheduleDiffEntity,
	scheduledLegEntity,
	setBookingsEffect,
	setLegsEffect,
	setUserLanguageEffect,
	signOutEffect,
	syncScheduleEffect,
	userEntity,
} from '@mopla/business-logic';
import { initI18n } from '@mopla/i18';
import { createLogger, ELogLevel } from '@mopla/logger';
import { PageLoadingIndicator } from '@mopla/ui';

import './styles.scss';
import './config/dayjs';

import { configureMoplaClient } from './api/configureMoplaClient';
import driverApi from './api/driverApi';
import { App } from './app/app';
import { initDriverSentry } from './app/initDriverSentry';
import { initFirebaseApp } from './app/initFirebaseApp';
import { environment } from './environments/environment';

createLogger({
	minLevel: environment.env === 'production' ? ELogLevel.INFO : ELogLevel.DEBUG,
	enableConsole: environment.env !== 'production',
	enableSentry: true,
	sentryMinLevel: ELogLevel.INFO,
	sentryInstance: Sentry,
	environment: environment.env,
	enableRedaction: environment.env === 'production',
	enableAutoBreadcrumbs: environment.env === 'production',
	defaultContext: {
		app: 'driver',
		platform: Capacitor.getPlatform(),
	},
});

configureMoplaClient(environment.baseUrl);

// Initialize i18n with path prefix
initI18n({
	pathPrefix: environment.basename ? '' : '/',
});

const root = ReactDOM.createRoot(
	document.getElementById('root') as HTMLElement
);

(async () => {
	await initDriverSentry();

	// initialize firebase
	await initFirebaseApp();
	/** White list of environments where we add RxDB dev utilities. Actually all envs except the production */
	const shouldAddRxDBDebugUtils = [
		'development',
		'local',
		'pullrequest',
		'staging',
	].includes(environment.env);

	const db = await initDatabase(shouldAddRxDBDebugUtils);

	const bl = await bootstrapBusinessLogic(
		driverApi,
		[
			scheduledLegEntity,
			bookingEntity,
			dataChangesEntity,
			userEntity,
			scheduleDiffEntity,
			featureFlagEntity,
		],
		[],
		db
	);

	if (shouldAddRxDBDebugUtils) {
		/** For debug purposes */
		(window as any).bl = bl;
	}

	addMultipleEffects([
		setLegsEffect,
		editLegEffect,
		setBookingsEffect,
		editBookingEffect,
		initEventStreamEffect,
		syncScheduleEffect,
		resetScheduleDiffEffect,
		initPushProcessorEffect,
		fetchUserEffect,
		setUserLanguageEffect,
		signOutEffect,
	]);

	root.render(
		<BusinessLayerContext.Provider value={bl}>
			<LogoutWrapper logoutEl={<PageLoadingIndicator />}>
				<App />
			</LogoutWrapper>
		</BusinessLayerContext.Provider>
	);
})();
